import { forwardRef, useEffect, useId, useState } from 'react';
import { cn } from '@/lib/utils';
import { centsToInputValue, isPartialRupeeInput, parseRupeeInput } from '@/domain/money';
import { Field } from './Input';

export interface MoneyInputProps {
  /** Value in integer cents. */
  value: number;
  onChange: (cents: number) => void;
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  onBlur?: () => void;
}

/**
 * Money entry in rupees, reported in cents.
 *
 * It keeps its own text state while the user types, because "1." and "12.5"
 * are valid keystrokes on the way to a number but are not yet a value. Only
 * complete input is parsed and pushed up, so the field never fights the user
 * by reformatting mid-keystroke.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { value, onChange, label, error, hint, required, disabled, placeholder = '0', className, autoFocus, onBlur },
  ref,
) {
  const id = useId();
  const [text, setText] = useState(() => centsToInputValue(value));

  // Re-sync when the value is changed from outside (auto-allocate, "pay in
  // full"), but never while the user is mid-edit of the same value.
  useEffect(() => {
    const parsed = parseRupeeInput(text);
    if (parsed !== value) setText(centsToInputValue(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Field label={label} error={error} hint={hint} required={required} htmlFor={id}>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">
          LKR
        </span>

        <input
          ref={ref}
          id={id}
          type="text"
          inputMode="decimal"
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          value={text}
          onChange={(event) => {
            const next = event.target.value;
            // Reject anything that could not become a valid amount, so the
            // field simply refuses stray characters rather than showing an error.
            if (!isPartialRupeeInput(next)) return;

            setText(next);
            onChange(parseRupeeInput(next) ?? 0);
          }}
          onBlur={() => {
            setText(centsToInputValue(parseRupeeInput(text) ?? 0));
            onBlur?.();
          }}
          className={cn(
            'tabular h-10 w-full rounded-lg border bg-white pl-11 pr-3 text-right text-sm font-medium text-slate-900',
            'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30',
            'disabled:bg-slate-50 disabled:text-slate-500',
            error ? 'border-red-400' : 'border-slate-300',
            className,
          )}
        />
      </div>
    </Field>
  );
});
